import { Button, Col, Row, Tooltip, Typography } from 'antd';
import { FullscreenOutlined, PlusOutlined } from '@ant-design/icons';
import {
  CustomNotification,
  Fieldset,
  FormAddButton,
  ThumbnailItem,
  WithAbsoluteContainer,
} from 'common/components';

import './ProductOrientation.less';
import PreviewOrientationModal from '../preview-modal/PreviewOrientationModal';
import { useMemo, useState } from 'react';
import { useGetProductFullView } from 'hooks';
import { useParams } from 'react-router-dom';
import noImage from 'assets/product-no-image.jpg';
import AddAssetPimModal from '../modal/add-asset-pim-modal';
import { useGetProductOrientationAssets } from 'hooks/product/useGetProductOrientationAssets';
import { isArray } from 'lodash';
import { mapFileTypeToImage } from 'utils/fileType';
import * as getLink from 'utils/common/linkBuilder';
import PreviewableThumb from 'common/components/thumb/PreviewableThumb';
import * as productService from 'services/product';

const ProductOrientation = (props) => {
  const { isAllowEdit, getProductDetail, getImagery } = props;
  const { id } = useParams();

  const { productFull } = useGetProductFullView({ productId: id });
  const { data, refetch } = useGetProductOrientationAssets({ productId: id });

  const mainImage = useMemo(() => {
    if (isArray(data?.gridData)) {
      return data?.gridData.find(
        (val) => val?.associateType === 'ORIENTATION' && val?.facing === 'Main'
      );
    }
    return;
  }, [data]);

  const leftOriented = useMemo(() => {
    if (isArray(data?.gridData)) {
      return data?.gridData.find(
        (val) =>
          val?.associateType === 'ORIENTATION' &&
          val?.facing === 'Left' &&
          val?.angle === 'No Plunge'
      );
    }
    return;
  }, [data]);

  const leftOrientedATC = useMemo(() => {
    if (isArray(data?.gridData)) {
      return data?.gridData.find(
        (val) =>
          val?.associateType === 'ORIENTATION' &&
          val?.facing === 'Left' &&
          val?.angle === 'Left'
      );
    }
    return;
  }, [data]);

  const rightOriented = useMemo(() => {
    if (isArray(data?.gridData)) {
      return data?.gridData.find(
        (val) =>
          val?.associateType === 'ORIENTATION' &&
          val?.facing === 'Right' &&
          val?.angle === 'No Plunge'
      );
    }
    return;
  }, [data]);

  const rightOrientedATC = useMemo(() => {
    if (isArray(data?.gridData)) {
      return data?.gridData.find(
        (val) =>
          val?.associateType === 'ORIENTATION' &&
          val?.facing === 'Right' &&
          val?.angle === 'Right'
      );
    }
    return;
  }, [data]);

  const imageList = useMemo(() => {
    let arrImageList = [];
    if (mainImage) arrImageList = [mainImage];
    if (leftOriented) arrImageList = [...arrImageList, leftOriented];
    if (leftOrientedATC) arrImageList = [...arrImageList, leftOrientedATC];
    if (rightOriented) arrImageList = [...arrImageList, rightOriented];
    if (rightOrientedATC) arrImageList = [...arrImageList, rightOrientedATC];
    return arrImageList;
  }, [
    mainImage,
    leftOriented,
    leftOrientedATC,
    rightOriented,
    rightOrientedATC,
  ]);

  const [isOpenPreview, setIsOpenReview] = useState(false);
  const [isLinkToNewAssetModalOpen, setIsLinkToNewAssetModalOpen] =
    useState(false);

  const handleToggleLinkAssetModal = () => {
    setIsLinkToNewAssetModalOpen((prev) => !prev);
  };

  const handleGetImage = () => {
    refetch();
    getImagery();
  };

  const showAddBtn = isAllowEdit;

  return (
    <div className='product-orientation'>
      <Row className={`product-orientation-wrapper__slider `} gutter={[8, 0]}>
        <Tooltip title='Show preview'>
          <Button
            onClick={() => setIsOpenReview(true)}
            className='product-orientation-wrapper__slider--open-button'
            icon={<FullscreenOutlined style={{ fontSize: '20px' }} />}
          ></Button>
        </Tooltip>
        <Col flex={0} style={{ width: '52px' }}>
          <Typography.Text className='product-orientation-wrapper__slider-title'>
            Orientation
          </Typography.Text>
        </Col>
        <Col
          flex={0}
          style={{ maxWidth: 'calc(100% - 60px)', width: '100%' }}
          className='product-orientation-wrapper__slider-container-wrapper '
        >
          <Row gutter={[8, 8]} style={{ height: '100%' }}>
            <Col span={12}>
              <OrientationImage
                title={'Main Image'}
                item={mainImage}
                isAllowEdit={isAllowEdit}
                productFull={productFull}
                getProductDetail={getProductDetail}
              />
            </Col>
            <Col span={12}>
              <OrientationImage
                title={'Left Oriented'}
                item={leftOriented}
                isAllowEdit={isAllowEdit}
                productFull={productFull}
                getProductDetail={getProductDetail}
              />
            </Col>
            <Col span={12}>
              <OrientationImage
                title={'Left Oriented Angled to Camera'}
                item={leftOrientedATC}
                isAllowEdit={isAllowEdit}
                productFull={productFull}
                getProductDetail={getProductDetail}
              />
            </Col>

            <Col span={12}>
              <OrientationImage
                title={'Right Oriented'}
                item={rightOriented}
                isAllowEdit={isAllowEdit}
                productFull={productFull}
                getProductDetail={getProductDetail}
              />
            </Col>
            <Col span={12}>
              <OrientationImage
                title={'Right Oriented Angled to Camera'}
                item={rightOrientedATC}
                isAllowEdit={isAllowEdit}
                productFull={productFull}
                getProductDetail={getProductDetail}
              />
            </Col>
          </Row>
        </Col>
        <Col span={1}></Col>

        {showAddBtn && (
          <FormAddButton
            text={null}
            icon={<PlusOutlined />}
            style={{ position: 'absolute', bottom: 4, right: 14 }}
            onClick={handleToggleLinkAssetModal}
          />
        )}
        <WithAbsoluteContainer>
          {isLinkToNewAssetModalOpen && (
            <AddAssetPimModal
              onCloseModal={handleToggleLinkAssetModal}
              addType={'Orientation'}
              getImagery={handleGetImage}
            />
          )}
        </WithAbsoluteContainer>
      </Row>
      <PreviewOrientationModal
        visible={isOpenPreview}
        productInfo={productFull}
        onCloseModal={() => {
          setIsOpenReview(false);
        }}
        imageList={imageList}
        getImagery={handleGetImage}
        titleModal={''}
        title={'Orientation'}
      />
    </div>
  );
};

const OrientationImage = ({
  title,
  item,
  isAllowEdit,
  getProductDetail,
  productFull,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handlePrimaryProduct = (productImageLinkId) => {
    setIsLoading(true);

    const params = {
      productId: productFull?.productId,
      productImageLinkId: productImageLinkId,
    };

    productService
      .setPrimaryProductImage(params)
      .then((response) => {
        if (response?.isSuccess) {
          CustomNotification.success('Change Primary Image successfully.');
          getProductDetail();
        } else {
          CustomNotification.error(response?.message);
        }
      })
      .catch((e) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };
  const showThumbMarketingImage = (item) => {
    return (
      item?.thumb500 ||
      item?.thumb1000 ||
      item?.thumbFull ||
      mapFileTypeToImage(item?.fileType)
    );
  };

  const showPreviewMarkingImage = (item) => {
    return (
      item?.thumbFull ||
      item?.thumb1000 ||
      item?.thumb500 ||
      mapFileTypeToImage(item?.fileType)
    );
  };

  const imgTile = getLink.assetLink(
    item?.digitalAssetId,
    item?.assetName,
    '_blank'
  );
  return (
    <Fieldset title={title}>
      <Row
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {item ? (
          <PreviewableThumb
            id={item?.productImageLinkId || title}
            img={item ? showThumbMarketingImage(item) : noImage}
            link={showPreviewMarkingImage(item)}
            title={imgTile}
            type='images'
            downloadLink={item?.originalFile}
            isDocument={false}
            isMarketing={true}
            isSetPrimary={isAllowEdit}
            isLoadingBtnPrimary={isLoading}
            handlePrimaryProduct={() =>
              handlePrimaryProduct(item?.productImageLinkId)
            }
          />
        ) : (
          <ThumbnailItem
            labelThumbnail={imgTile}
            imageThumbnail={item ? showThumbMarketingImage(item) : noImage}
            hideCheckbox
            hideLabel
            style={{ marginLeft: 0 }}
          />
        )}
      </Row>
    </Fieldset>
  );
};

export default ProductOrientation;
